import { readWithAliases, Keys } from "../lib/storage/keys";
import { storage } from "../lib/storage/storage";

export type OfflineServiceId =
	| "DTF_UV"
	| "DTF_TEXTIL"
	| "CAMISETA"
	| "SUBLIMACAO";

export interface OfflineServiceDef {
	label: string;
	folder: string;
}

export interface MaterialInferenceRule {
	match: string[];
	serviceId: OfflineServiceId;
}

export interface ExportProducaoOfflineConfig {
	defaultRoot: string;
	roots: string[];
	horarioCorte: string;
	services: Record<OfflineServiceId, OfflineServiceDef>;
	materialInference: MaterialInferenceRule[];
}

/** Espelha exportProducaoOffline do config.json do addon (fallback estático). */
export const DEFAULT_EXPORT_PRODUCAO_OFFLINE_CONFIG: ExportProducaoOfflineConfig =
	{
		defaultRoot: "\\\\192.168.100.183\\FileSecuritySystemProd\\PRODUCAO",
		roots: ["\\\\192.168.100.183\\FileSecuritySystemProd\\PRODUCAO"],
		horarioCorte: "16:00",
		services: {
			DTF_UV: { label: "DTF UV", folder: "DTF UV" },
			DTF_TEXTIL: { label: "DTF TEXTIL", folder: "DTF TEXTIL" },
			CAMISETA: { label: "CAMISETA", folder: "CAMISETA" },
			SUBLIMACAO: { label: "SUBLIMACAO", folder: "SUBLIMACAO" },
		},
		materialInference: [
			{ match: ["dtf", "textil"], serviceId: "DTF_TEXTIL" },
			{ match: ["dtf", "uv"], serviceId: "DTF_UV" },
			{ match: ["dtf"], serviceId: "DTF_TEXTIL" },
			{ match: ["camiseta", "uniforme"], serviceId: "CAMISETA" },
			{ match: ["sublim", "subli"], serviceId: "SUBLIMACAO" },
		],
	};

let cachedRuntimeConfig: ExportProducaoOfflineConfig | null = null;

/** Permite injetar config lida do addon quando houver handler C# futuro. */
export function setExportProducaoOfflineRuntimeConfig(
	config: Partial<ExportProducaoOfflineConfig>,
): void {
	cachedRuntimeConfig = {
		...DEFAULT_EXPORT_PRODUCAO_OFFLINE_CONFIG,
		...config,
		services: {
			...DEFAULT_EXPORT_PRODUCAO_OFFLINE_CONFIG.services,
			...(config.services ?? {}),
		},
		materialInference:
			config.materialInference ??
			DEFAULT_EXPORT_PRODUCAO_OFFLINE_CONFIG.materialInference,
	};
}

export function getExportProducaoOfflineConfig(): ExportProducaoOfflineConfig {
	return cachedRuntimeConfig ?? DEFAULT_EXPORT_PRODUCAO_OFFLINE_CONFIG;
}

interface SavedRulesLike {
	diretorio_base_export_prod?: string;
}

function getCachedBaseDirFromRules(): string | undefined {
	const saved = readWithAliases<Record<string, unknown>>(
		Keys.export.currentSaveData,
		["currentSaveData"],
		{ area: "session" },
	);
	const regras = saved?.regras as SavedRulesLike | undefined;
	return regras?.diretorio_base_export_prod?.trim() || undefined;
}

/** Raízes disponíveis no dropdown: config + cache de regras + última escolha. */
export function getAvailableOfflineRoots(): string[] {
	const config = getExportProducaoOfflineConfig();
	const seen = new Set<string>();
	const roots: string[] = [];

	const add = (value: string | undefined | null) => {
		const trimmed = value?.trim();
		if (!trimmed || seen.has(trimmed)) return;
		seen.add(trimmed);
		roots.push(trimmed);
	};

	for (const r of config.roots) add(r);
	add(config.defaultRoot);
	add(getCachedBaseDirFromRules());
	add(storage.get<string>(Keys.export.offlineLastRoot, { area: "local" }));
	add(storage.get<string>(Keys.export.offlineFallbackRoot, { area: "local" }));

	return roots;
}

export function getDefaultOfflineRoot(): string {
	const persisted = storage.get<string>(Keys.export.offlineLastRoot, {
		area: "local",
	});
	if (persisted?.trim()) return persisted.trim();

	const roots = getAvailableOfflineRoots();
	if (roots.length > 0) return roots[0];

	return getExportProducaoOfflineConfig().defaultRoot;
}

export function persistOfflineRoot(root: string): void {
	const trimmed = root.trim();
	if (!trimmed) return;
	storage.set(Keys.export.offlineLastRoot, trimmed, { area: "local" });
}

export function listOfflineServiceOptions(): Array<{
	id: OfflineServiceId;
	label: string;
	folder: string;
}> {
	const config = getExportProducaoOfflineConfig();
	return (Object.keys(config.services) as OfflineServiceId[]).map((id) => ({
		id,
		label: config.services[id].label,
		folder: config.services[id].folder,
	}));
}
